import { useState, useEffect, useRef } from 'react';
import { authFetch, API } from './api';

// Lightweight markdown renderer tuned to the negotiation brief output.
// Splits the report by `## XYZ` so each section gets its own card style.
function splitSections(md) {
  if (!md) return [];
  const lines = md.split('\n');
  const sections = [];
  let current = null;
  for (const line of lines) {
    const m = line.match(/^##\s+(.+)$/);
    if (m) {
      if (current) sections.push(current);
      current = { heading: m[1].trim(), lines: [] };
    } else {
      if (!current) current = { heading: null, lines: [] };
      current.lines.push(line);
    }
  }
  if (current) sections.push(current);
  return sections;
}

function renderInlineBold(s, keyPrefix) {
  const parts = [];
  const re = /\*\*([^*]+)\*\*/g;
  let last = 0;
  let m;
  let i = 0;
  while ((m = re.exec(s)) !== null) {
    if (m.index > last) parts.push(<span key={`${keyPrefix}-t${i++}`}>{s.slice(last, m.index)}</span>);
    parts.push(<strong key={`${keyPrefix}-b${i++}`} className="text-white font-semibold">{m[1]}</strong>);
    last = m.index + m[0].length;
  }
  if (last < s.length) parts.push(<span key={`${keyPrefix}-t${i++}`}>{s.slice(last)}</span>);
  return parts;
}

function renderBlocks(lines, opts = {}) {
  const blocks = [];
  let cur = null;
  for (const raw of lines) {
    const line = raw.trimEnd();
    const numbered = line.match(/^\s*(\d+)\.\s+(.+)$/);
    const bullet = line.match(/^\s*[-*]\s+(.+)$/);
    if (numbered) {
      if (cur?.type !== 'ol') {
        if (cur) blocks.push(cur);
        cur = { type: 'ol', items: [] };
      }
      cur.items.push({ num: numbered[1], text: numbered[2] });
    } else if (bullet) {
      if (cur?.type !== 'ul') {
        if (cur) blocks.push(cur);
        cur = { type: 'ul', items: [] };
      }
      cur.items.push(bullet[1]);
    } else if (line.trim() === '') {
      if (cur) { blocks.push(cur); cur = null; }
    } else {
      if (cur?.type !== 'p') {
        if (cur) blocks.push(cur);
        cur = { type: 'p', lines: [] };
      }
      cur.lines.push(line);
    }
  }
  if (cur) blocks.push(cur);

  return blocks.map((b, i) => {
    if (b.type === 'ol') {
      return (
        <ol key={i} className="space-y-2 my-2">
          {b.items.map((it, j) => (
            <li key={j} className="flex gap-3 items-start">
              <span className={`shrink-0 ${opts.bigNumbers ? 'w-9 h-9 text-base' : 'w-7 h-7 text-sm'} rounded-full flex items-center justify-center font-bold bg-[#00E676] text-[#0A0E27]`}>
                {it.num}
              </span>
              <span className="text-gray-200 text-sm leading-relaxed pt-1.5">{renderInlineBold(it.text, `ol-${i}-${j}`)}</span>
            </li>
          ))}
        </ol>
      );
    }
    if (b.type === 'ul') {
      return (
        <ul key={i} className="space-y-1.5 my-2">
          {b.items.map((it, j) => (
            <li key={j} className="flex gap-2 items-start text-gray-200 text-sm">
              <span className="shrink-0 text-[#FF6B35] mt-1 leading-none">●</span>
              <span className="flex-1">{renderInlineBold(it, `ul-${i}-${j}`)}</span>
            </li>
          ))}
        </ul>
      );
    }
    return (
      <p key={i} className="text-gray-200 text-sm leading-relaxed my-2">
        {renderInlineBold(b.lines.join(' '), `p-${i}`)}
      </p>
    );
  });
}

// Per-section accent — Leverage Points get orange ● markers + big numbers;
// Recommended Approach numbers render large green; the rest get cyan accents.
const SECTION_STYLE = {
  'NEGOTIATION SUMMARY':    { accent: '#00D4FF', label: '🎯 Position' },
  'LEVERAGE POINTS':        { accent: '#FF6B35', label: '⚡ Leverage' },
  'RECOMMENDED APPROACH':   { accent: '#00E676', label: '📋 Approach', bigNumbers: true },
  'LESSONS FROM PAST LOSSES': { accent: '#EF4444', label: '🧭 Lessons' },
  'RISK FACTORS':           { accent: '#FFC107', label: '⚠️ Risks' },
  'TALKING POINTS':         { accent: '#9C27B0', label: '🗣 Scripts' },
};

/**
 * Negotiation Brief panel — reusable in both the PropertyEvaluation modal
 * and the Reports flow. Caller provides propertyId + buyerId.
 *
 * autoGenerate=true triggers the fetch on mount.
 */
export default function NegotiationBriefPanel({ propertyId, buyerId, autoGenerate = false }) {
  const [loading, setLoading] = useState(false);
  const [brief, setBrief] = useState(null);
  const [meta, setMeta] = useState({});
  const [error, setError] = useState('');
  const [copied, setCopied] = useState(false);
  // `generated` flips to true only after a successful fetch — until then the
  // panel shows its initial state (the big "Generate Negotiation Brief" button).
  // We don't seed this from autoGenerate, because that caused the loaded-state
  // UI (with the "Regenerate" button) to flash before any brief existed.
  const [generated, setGenerated] = useState(false);
  // Track whether the autoGenerate trigger has already fired this mount so we
  // don't re-fire on every state update.
  const autoFired = useRef(false);

  const generate = async () => {
    setLoading(true);
    setError('');
    setCopied(false);
    try {
      const res = await authFetch(`${API}/api/insights/negotiation/${propertyId}/buyer/${buyerId}`);
      if (!res.ok) {
        const body = await res.json().catch(() => ({}));
        throw new Error(body.detail || `Request failed (${res.status})`);
      }
      const data = await res.json();
      setBrief(data.brief || '');
      setMeta({
        property_name: data.property_name,
        property_address: data.property_address,
        client_name: data.client_name,
        deal_stage: data.deal_stage,
      });
      setGenerated(true);
    } catch (e) {
      setError(e.message || 'Could not generate brief.');
    } finally {
      setLoading(false);
    }
  };

  const copyToClipboard = async () => {
    if (!brief) return;
    try {
      await navigator.clipboard.writeText(brief);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    } catch (_) {
      const ta = document.createElement('textarea');
      ta.value = brief;
      document.body.appendChild(ta);
      ta.select();
      document.execCommand('copy');
      document.body.removeChild(ta);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    }
  };

  // Open a clean print template in a new tab and trigger print. We render the
  // brief as static HTML rather than asking the host page to print itself —
  // that avoids printing the modal overlay or the surrounding eval-page chrome.
  const handlePrint = () => {
    if (!brief) return;
    const w = window.open('', '_blank');
    if (!w) return;

    const escapeHtml = (s) => String(s == null ? '' : s)
      .replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/>/g, '&gt;')
      .replace(/"/g, '&quot;').replace(/'/g, '&#39;');

    // Inline-format **bold** spans inside escaped text
    const renderInline = (s) => escapeHtml(s).replace(/\*\*([^*]+)\*\*/g, '<strong>$1</strong>');

    // Group consecutive list items / paragraphs and emit clean HTML blocks
    const renderBlocks = (lines) => {
      const out = [];
      let cur = null;
      const flush = () => { if (cur) { out.push(cur); cur = null; } };
      for (const rawLine of lines) {
        const line = rawLine.trimEnd();
        const numbered = line.match(/^\s*(\d+)\.\s+(.+)$/);
        const bullet = line.match(/^\s*[-*]\s+(.+)$/);
        if (numbered) {
          if (cur?.type !== 'ol') { flush(); cur = { type: 'ol', items: [] }; }
          cur.items.push(numbered[2]);
        } else if (bullet) {
          if (cur?.type !== 'ul') { flush(); cur = { type: 'ul', items: [] }; }
          cur.items.push(bullet[1]);
        } else if (line.trim() === '') {
          flush();
        } else {
          if (cur?.type !== 'p') { flush(); cur = { type: 'p', lines: [] }; }
          cur.lines.push(line);
        }
      }
      flush();
      return out.map((b) => {
        if (b.type === 'ol') {
          return `<ol class="ol">${b.items.map(i => `<li>${renderInline(i)}</li>`).join('')}</ol>`;
        }
        if (b.type === 'ul') {
          return `<ul class="ul">${b.items.map(i => `<li>${renderInline(i)}</li>`).join('')}</ul>`;
        }
        return `<p>${renderInline(b.lines.join(' '))}</p>`;
      }).join('');
    };

    const sectionsHtml = splitSections(brief).map((s) => {
      const heading = s.heading ? `<h2>${escapeHtml(s.heading)}</h2>` : '';
      return `<section class="brief-section">${heading}${renderBlocks(s.lines)}</section>`;
    }).join('');

    const title = `Negotiation Brief — ${meta.property_name || meta.property_address || 'Property'}`;
    const today = new Date().toLocaleDateString(undefined, {
      weekday: 'long', year: 'numeric', month: 'long', day: 'numeric',
    });

    w.document.write(`<!DOCTYPE html>
<html lang="en">
<head>
  <meta charset="utf-8" />
  <title>${escapeHtml(title)}</title>
  <style>
    * { box-sizing: border-box; }
    body {
      font-family: -apple-system, BlinkMacSystemFont, "Segoe UI", "Helvetica Neue", Arial, sans-serif;
      max-width: 820px; margin: 0 auto; padding: 40px;
      color: #1A1A1A; background: #ffffff; line-height: 1.55;
    }
    .header {
      border-bottom: 2px solid #FF6B35;
      padding-bottom: 16px; margin-bottom: 28px;
    }
    .header .label {
      font-size: 11px; color: #888; letter-spacing: 2px; text-transform: uppercase;
    }
    .header h1 {
      font-size: 24px; margin: 6px 0 4px 0; color: #1A1A1A;
    }
    .header .meta { color: #555; font-size: 13px; }
    .brief-section {
      margin: 0 0 22px 0; padding: 16px 18px;
      background: #fafafa; border-left: 4px solid #9CA3AF;
      border-radius: 0 6px 6px 0;
      page-break-inside: avoid;
      break-inside: avoid;
    }
    /* Per-section accent stripes, matching the on-screen palette */
    .brief-section:nth-of-type(1) { border-left-color: #00BCD4; }
    .brief-section:nth-of-type(2) { border-left-color: #FF6B35; }
    .brief-section:nth-of-type(3) { border-left-color: #00C853; }
    .brief-section:nth-of-type(4) { border-left-color: #EF4444; }
    .brief-section:nth-of-type(5) { border-left-color: #FFC107; }
    .brief-section:nth-of-type(6) { border-left-color: #9C27B0; }
    h2 {
      font-size: 13px; letter-spacing: 1px; text-transform: uppercase;
      color: #555; margin: 0 0 10px 0; font-weight: 700;
    }
    p { margin: 8px 0; font-size: 14px; color: #1A1A1A; }
    strong { color: #0A0E27; }
    ol.ol, ul.ul { margin: 8px 0; padding-left: 22px; }
    ol.ol li, ul.ul li { margin: 6px 0; font-size: 14px; }
    ul.ul li::marker { color: #FF6B35; }
    .footer {
      margin-top: 32px; padding-top: 14px;
      border-top: 1px solid #E5E7EB;
      font-size: 11px; color: #888; text-align: center;
    }
    @media print {
      @page { size: letter; margin: 0.75in; }
      body { padding: 0; }
      .brief-section { page-break-inside: avoid; break-inside: avoid; }
      * { -webkit-print-color-adjust: exact; print-color-adjust: exact; }
    }
  </style>
</head>
<body>
  <div class="header">
    <div class="label">SchemeSync CRE — Negotiation Brief</div>
    <h1>${escapeHtml(meta.property_name || meta.property_address || 'Property')}</h1>
    <div class="meta">
      ${meta.client_name ? `Client: <strong>${escapeHtml(meta.client_name)}</strong> · ` : ''}${escapeHtml(today)}
    </div>
  </div>
  ${sectionsHtml}
  <div class="footer">Generated by SchemeSync CRE · ${escapeHtml(today)}</div>
</body>
</html>`);
    w.document.close();
    // Give the browser a tick to lay out before firing the print dialog
    setTimeout(() => {
      try { w.focus(); w.print(); } catch (_) {}
    }, 120);
  };

  // Auto-fire once on mount when the caller asked (e.g. the Reports flow
  // where the user already opted into generation upstream). Guarded by a ref
  // so React 18 strict-mode double-invokes don't fire twice.
  useEffect(() => {
    if (!autoGenerate || autoFired.current) return;
    autoFired.current = true;
    generate();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [autoGenerate]);

  if (!generated && !loading) {
    return (
      <div className="bg-[#1A2744] rounded-lg border-l-4 border-[#FF6B35] border border-[#546E7A]/30 p-5">
        <div className="flex items-center justify-between flex-wrap gap-3">
          <div>
            <h3 className="text-white text-lg font-semibold flex items-center gap-2">
              🤝 Negotiation Brief
            </h3>
            <p className="text-gray-400 text-sm mt-1">
              AI-generated strategy: leverage points, recommended position, talking points.
            </p>
          </div>
          <button
            onClick={generate}
            className="px-4 py-2 bg-[#FF6B35] text-white text-sm font-semibold rounded-lg hover:brightness-110 transition-all"
          >
            Generate Negotiation Brief
          </button>
        </div>
      </div>
    );
  }

  if (loading) {
    return (
      <div className="bg-[#1A2744] rounded-lg border-l-4 border-[#FF6B35] border border-[#546E7A]/30 p-5">
        <h3 className="text-white text-lg font-semibold flex items-center gap-2 mb-3">
          🤝 Negotiation Brief
        </h3>
        <div className="flex items-center gap-2 text-[#00D4FF] text-sm">
          <span className="inline-flex gap-0.5">
            <span className="animate-bounce" style={{ animationDelay: '0ms' }}>.</span>
            <span className="animate-bounce" style={{ animationDelay: '150ms' }}>.</span>
            <span className="animate-bounce" style={{ animationDelay: '300ms' }}>.</span>
          </span>
          <span>Building negotiation strategy…</span>
        </div>
      </div>
    );
  }

  const sections = splitSections(brief);

  return (
    <div className="space-y-4">
      {/* Header card with action buttons */}
      <div className="bg-[#1A2744] rounded-lg border-l-4 border-[#FF6B35] border border-[#546E7A]/30 p-5">
        <div className="flex items-start justify-between gap-3 flex-wrap">
          <div className="min-w-0">
            <h3 className="text-white text-lg font-semibold flex items-center gap-2">
              🤝 Negotiation Brief
            </h3>
            <p className="text-gray-400 text-xs mt-1 truncate">
              {meta.property_name || meta.property_address}
              {meta.client_name ? ` · ${meta.client_name}` : ''}
              {meta.deal_stage ? ` · ${meta.deal_stage.replace('_', ' ')}` : ''}
            </p>
          </div>
          <div className="flex gap-2 flex-wrap">
            <button
              onClick={copyToClipboard}
              disabled={!brief}
              className="px-3 py-1.5 bg-[#243B55] text-[#00D4FF] text-sm font-medium rounded-lg hover:bg-[#2d4a6a] transition-colors disabled:opacity-40"
            >
              {copied ? '✓ Copied' : 'Copy'}
            </button>
            <button
              onClick={handlePrint}
              disabled={!brief}
              className="px-3 py-1.5 bg-[#243B55] text-[#FFC107] text-sm font-medium rounded-lg hover:bg-[#2d4a6a] transition-colors disabled:opacity-40"
            >
              🖨 Print / Save as PDF
            </button>
            <button
              onClick={generate}
              className="px-3 py-1.5 bg-[#243B55] text-[#00E676] text-sm font-medium rounded-lg hover:bg-[#2d4a6a] transition-colors"
            >
              Regenerate
            </button>
          </div>
        </div>
        {error && (
          <div className="mt-3 px-3 py-2 text-sm text-red-300 bg-red-900/20 border border-red-500/30 rounded">
            {error}
          </div>
        )}
      </div>

      {/* Section cards */}
      {sections.map((s, i) => {
        const key = (s.heading || '').toUpperCase();
        const style = SECTION_STYLE[key] || { accent: '#546E7A', label: s.heading };
        return (
          <div
            key={i}
            className="bg-[#1A2744] rounded-lg border border-[#546E7A]/30 p-5"
            style={{ borderLeft: `4px solid ${style.accent}` }}
          >
            {s.heading && (
              <h4 className="text-white font-semibold text-sm uppercase tracking-wider mb-2">
                {s.heading}
              </h4>
            )}
            {renderBlocks(s.lines, { bigNumbers: !!style.bigNumbers })}
          </div>
        );
      })}

      {sections.length === 0 && brief && (
        <div className="bg-[#1A2744] rounded-lg border border-[#546E7A]/30 p-5">
          <p className="text-gray-300 text-sm whitespace-pre-wrap">{brief}</p>
        </div>
      )}
    </div>
  );
}
